import {
  CalendarDate,
  endOfMonth,
  endOfYear,
  startOfMonth,
  startOfYear,
  today,
} from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { Settings } from 'luxon';
import { useRef } from 'react';
import { mergeProps, useButton } from 'react-aria';
import { RangeCalendarState } from 'react-stately';
import styled, { css } from 'styled-components';

import { MenuItem, MenuList } from 'design-system/components/Menu';
import { isFocused, isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';

interface DateRangePreset {
  label: string;
  range: RangeValue<CalendarDate>;
}

const getDefaultPresets = (tz: string) => {
  const now = today(tz);
  return [
    {
      label: 'Last 7 days',
      range: {
        start: now.subtract({ days: 7 }),
        end: now,
      },
    },
    {
      label: 'Last 14 days',
      range: {
        start: now.subtract({ days: 14 }),
        end: now,
      },
    },
    {
      label: 'Last 30 days',
      range: {
        start: now.subtract({ days: 30 }),
        end: now,
      },
    },
    {
      label: 'This month',
      range: {
        start: startOfMonth(now),
        // Effectively, this should always be today, since we can't look forward
        end: now,
      },
    },
    {
      label: 'Last month',
      range: {
        start: startOfMonth(now.subtract({ months: 1 })),
        end: endOfMonth(now.subtract({ months: 1 })),
      },
    },
    {
      label: 'This year',
      range: {
        start: startOfYear(now),
        end: now,
      },
    },
    {
      label: 'Last year',
      range: {
        start: startOfYear(now.subtract({ years: 1 })),
        end: endOfYear(now.subtract({ years: 1 })),
      },
    },
  ];
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

interface PresetButtonProps {
  state: RangeCalendarState;
  preset: DateRangePreset;
}

const StyledPresetButton = styled(MenuItem)<{ isSelected?: boolean }>`
  ${({ isSelected }) =>
    isSelected
      ? css`
          background-color: ${({ theme }) => theme.color.background.highlight};
        `
      : css`
          &${isNotDisabled}${isHovered}, &${isNotDisabled}${isFocused} {
            background-color: transparent;
          }
        `}
`;

interface DateRangePickerPresetsProps {
  presetsFn?: (tz: string) => DateRangePreset[];
  state: RangeCalendarState;
  setDateMode: (mode: 'start' | 'end' | null) => void;
}

const DateRangePickerPresets = ({
  presetsFn = getDefaultPresets,
  state,
  setDateMode,
  ...props
}: DateRangePickerPresetsProps) => {
  const PresetButton = ({ state, preset, ...props }: PresetButtonProps) => {
    const ref = useRef<HTMLDivElement>();
    const { buttonProps } = useButton(
      {
        onPress: () => {
          state.setValue(preset.range);
          setDateMode(null);
        },
      },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ref,
    );

    // Comparing CalendarDate objects is a disaster, they must be translated
    const selectedButton = state.value
      ? preset.range.start.toLocaleString() === state.value.start.toLocaleString() &&
        preset.range.end.toLocaleString() === state.value.end.toLocaleString()
      : false;

    return (
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      <StyledPresetButton isSelected={selectedButton} {...mergeProps(props, buttonProps)} ref={ref}>
        {preset.label}
      </StyledPresetButton>
    );
  };

  // The reason that we need to do this at render time is because the default timezone for moment is set after
  // the current User's timezone is loaded. Thus we woudln't be getting the right timezone unless if we defer getting
  // the timezone.
  const presets = presetsFn(Settings.defaultZone.name);
  return (
    <Container {...props}>
      <MenuList>
        {presets.map((preset, i) => (
          <PresetButton key={i} preset={preset} state={state} />
        ))}
      </MenuList>
    </Container>
  );
};

export default DateRangePickerPresets;
